import { autoflowColors, autoflowScale } from './autoflow-colors.js';

const colors = {
  success: { anchor: autoflowColors.success, textShade: 900 },
  danger: { anchor: autoflowColors.danger, textShade: 800 },
  warn: { anchor: autoflowColors.warn, textShade: 900 },
  info: { anchor: autoflowColors.info, textShade: 900 },
};

export const autoflowFeedbackSemantic = Object.fromEntries(
  Object.entries(colors).map(([name, { anchor, textShade }]) => [
    name,
    {
      ...autoflowScale(anchor),
      color: `light-dark({${name}.500}, {${name}.200})`,
      textColor: `light-dark({${name}.${textShade}}, {${name}.200})`,
      hoverColor: `light-dark({${name}.400}, {${name}.100})`,
      activeColor: `light-dark({${name}.300}, {${name}.50})`,
      contrastColor:
        name === 'danger'
          ? `light-dark(${autoflowColors.white}, ${autoflowColors.ink})`
          : autoflowColors.ink,
      background: `light-dark({${name}.50}, {${name}.950})`,
      borderColor: `light-dark({${name}.200}, {${name}.700})`,
      badgeBackground: `light-dark({${name}.500}, {${name}.200})`,
      badgeColor:
        name === 'danger'
          ? `light-dark(${autoflowColors.white}, ${autoflowColors.ink})`
          : autoflowColors.ink,
    },
  ]),
);

const entries = (create) =>
  Object.fromEntries(Object.keys(colors).map((name) => [name, create(name)]));
const badge = (name) => ({
  background: `{${name}.badge.background}`,
  color: `{${name}.badge.color}`,
});
const textButton = (name) => ({
  color: `{${name}.text.color}`,
  hoverBackground: `{${name}.background}`,
  activeBackground: `light-dark({${name}.100}, {${name}.900})`,
});
const feedback = (name) => ({
  background: `{${name}.background}`,
  borderColor: `{${name}.border.color}`,
  color: `{${name}.text.color}`,
  closeButton: {
    hoverBackground: `light-dark({${name}.100}, {${name}.900})`,
    focusRing: { color: '{primary.color}', shadow: '{focus.ring.shadow}' },
  },
});
const message = (name) => ({
  ...feedback(name),
  outlined: { color: `{${name}.text.color}`, borderColor: `{${name}.text.color}` },
  simple: { color: `{${name}.text.color}` },
});

// Lara maps each component's severities directly to primitive colors. Route
// these through shared meanings so buttons, badges and feedback agree.
export const autoflowFeedbackComponents = {
  tag: entries(badge),
  badge: entries(badge),
  button: {
    root: entries((name) => ({
      background: `{${name}.color}`,
      hoverBackground: `{${name}.hover.color}`,
      activeBackground: `{${name}.active.color}`,
      borderColor: `{${name}.color}`,
      hoverBorderColor: `{${name}.hover.color}`,
      activeBorderColor: `{${name}.active.color}`,
      color: name === 'warn' ? autoflowColors.ink : autoflowColors.white,
      hoverColor: name === 'warn' ? autoflowColors.ink : autoflowColors.white,
      activeColor: name === 'warn' ? autoflowColors.ink : autoflowColors.white,
      focusRing: { color: '{primary.color}', shadow: '{focus.ring.shadow}' },
    })),
    outlined: entries((name) => ({ ...textButton(name), borderColor: `{${name}.text.color}` })),
    text: entries(textButton),
  },
  message: {
    info: message('info'),
    success: message('success'),
    warn: message('warn'),
    error: message('danger'),
  },
  toast: {
    info: feedback('info'),
    success: feedback('success'),
    warn: feedback('warn'),
    error: feedback('danger'),
  },
};
